import { AxiosResponse } from 'axios'
import { camelize } from 'casing'
import { RootState, AppDispatch } from '../../../store'

import {
  ReferralObject,
  SortDirection,
  AwsProducts,
  ApiResponseProduct,
  PartnerCentralUsers,
  FiltersState,
  DashboardMetrics,
  AgentRecommendations,
  AgentDrawerType,
} from './reducer'

import { startLoading, stopLoading, LoadingTypes } from '@labrav/common'
import { isAdminRole } from '../../../common/utils/isAdminRole'
import { PartnerType } from '../../../common/modules/partner/action'
import { ProductType } from '../../../common/modules/partner/reducer'
import { getPartnerIdFromState } from '../../../common/utils/Hooks/usePartnerData'

import {
  fetchMetrics,
  fetchReferrals,
  fetchPartnerCentralUsers,
  fetchCRMfiels,
  fetchAWSReferrals,
  fetchTestOpportunity,
  putTestOpportunity,
  createTestOpportunity,
  approveTestOpportunity,
  reSyncReferralAPI,
  fetchSolutions,
  fetchDashboardMetricsAPI,
  fetchAgentRecommendationsAPI,
} from '../../../common/api/CoSell'
import { getReferralObjectTransformed } from '../../../common/utils/CoSellHelpers/helperFunctions'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
import { warningMapping } from '../../../common/utils/cosellFilterMapping'
import { getErrorMessages } from '../../../common/utils/error'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { errorLogger } from '../../../common/utils/errorLogger'
import {
  FormField,
  SelectorOptions,
} from '@labrav/react-components/lib/@types/types/forms'
import { TestOpportunitySaveSuccess } from '../../../common/utils/messagesContants'
import { isEmpty } from 'lodash'
import { format, addDays, subDays, subHours } from 'date-fns'
import { canvasAuthService } from '../../../common/services/CanvasAuthService'
import { COSELL_ACTIONS, ReferralFormField } from './types'
import { getPartnerSolutions } from '../../api'

export { COSELL_ACTIONS }
export type { ReferralFormField }

export const setReferrals = (referralObject: {
  referral: ReferralObject[]
  count: number
  pageSize: number
  pageNumber: number
  hasMore: boolean
}) => ({
  type: COSELL_ACTIONS.SET_REFERRALS,
  payload: referralObject,
})

export const clearReferralsData = () => ({
  type: COSELL_ACTIONS.CLEAR_REFERRALS_DATA,
})

export const setAwsProducts = (awsProducts: SelectorOptions[]) => ({
  type: COSELL_ACTIONS.SET_AWS_PRODUCTS,
  payload: awsProducts,
})

export const setPartnerCentralUsersData = (data: PartnerCentralUsers[]) => ({
  type: COSELL_ACTIONS.SET_PARTNER_CENTRAL_USER_DETAILS,
  payload: data,
})

export const setTestOpportunityData = (data: object | undefined) => ({
  type: COSELL_ACTIONS.SET_TEST_OPPORTUNITY,
  payload: data,
})

export const setReferralsMetrics = (
  referralMetrics: Record<string, unknown>
) => ({
  type: COSELL_ACTIONS.SET_REFERRALS_METRICS,
  payload: referralMetrics,
})

export const setSolutionOfferedOptions = (
  solutionOfferedOptions: SelectorOptions[]
) => ({
  type: COSELL_ACTIONS.SET_SOLUTION_OFFERED_OPTIONS,
  payload: solutionOfferedOptions,
})

type AwsSolutionOffering = {
  offering_id: string
  offering_title: string
  offering_type?: string
  created_date?: string
  offering_status?: string | null
}

export type SolutionOfferedOptionWithStatus = SelectorOptions & {
  offering_status?: string | null
}

export const mapAwsSolutionsToSelectorOptions = (
  awsSolutions: AwsSolutionOffering[]
): SolutionOfferedOptionWithStatus[] =>
  awsSolutions.map(solution => ({
    label: solution.offering_title,
    value: solution.offering_id,
    offering_status: solution.offering_status ?? null,
  }))

const normalizeSolutionMatchKey = (value: string): string =>
  value.trim().toLowerCase()

export const enrichSolutionOfferedOptionsWithAwsStatus = (
  options: SelectorOptions[],
  awsSolutions: AwsSolutionOffering[]
): SolutionOfferedOptionWithStatus[] => {
  const statusById = new Map(
    awsSolutions.map(solution => [
      solution.offering_id,
      solution.offering_status ?? null,
    ])
  )
  const statusByTitle = new Map(
    awsSolutions.map(solution => [
      normalizeSolutionMatchKey(solution.offering_title),
      solution.offering_status ?? null,
    ])
  )

  const resolveOfferingStatus = (
    option: SelectorOptions
  ): string | null | undefined => {
    const value = String(option.value)

    if (value === 'Other') {
      return undefined
    }

    if (statusById.has(value)) {
      return statusById.get(value) ?? null
    }

    const normalizedLabel = normalizeSolutionMatchKey(String(option.label))
    if (statusByTitle.has(normalizedLabel)) {
      return statusByTitle.get(normalizedLabel) ?? null
    }

    const normalizedValue = normalizeSolutionMatchKey(value)
    if (statusByTitle.has(normalizedValue)) {
      return statusByTitle.get(normalizedValue) ?? null
    }

    return null
  }

  return options.map(option => {
    const offeringStatus = resolveOfferingStatus(option)

    if (offeringStatus === undefined) {
      return option
    }

    return {
      ...option,
      offering_status: offeringStatus,
    }
  })
}

export const solutionOfferedOptionsHaveAwsStatus = (
  options: SelectorOptions[] | undefined
): boolean => {
  if (!options?.length) {
    return false
  }
  const hasAwsMapping = options.some(
    option =>
      (option as SolutionOfferedOptionWithStatus).offering_status !== undefined
  )
  if (!hasAwsMapping) {
    return false
  }
  return options.some(
    option =>
      typeof (option as SolutionOfferedOptionWithStatus).offering_status ===
      'string'
  )
}

const getPartnerIdForCosellActions = (getState: () => RootState): string => {
  const state = getState()
  const hubspotPartnerId =
    state.customForm?.hubspotForm?.customData?.referralMetadata?.partner_id
  if (hubspotPartnerId) {
    return hubspotPartnerId
  }

  const isAdmin = isAdminRole(state.userProfile.userProfile)
  const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
  return (
    getPartnerIdFromState(state, window.location.pathname, partnerType) || ''
  )
}

const loadSolutionOfferedFromAwsEngine = async (
  dispatch: AppDispatch,
  productId: ProductType,
  partnerId: string,
  getState: () => RootState
): Promise<void> => {
  const response = await fetchSolutions(partnerId)
  const awsSolutions = response.data.cloud_config?.aws_solutions || []
  const existingOptions = getState().cosell[productId]?.solutionOfferedOptions
  const solutionOptions =
    existingOptions?.length > 0
      ? enrichSolutionOfferedOptionsWithAwsStatus(existingOptions, awsSolutions)
      : mapAwsSolutionsToSelectorOptions(awsSolutions)

  await dispatch(
    actionTypeWrapper(productId, setSolutionOfferedOptions(solutionOptions))
  )
}

export type FetchSolutionsOfferedOptions = {
  silent?: boolean
}

export const setReferralFormFields = (
  referralFormFields: ReferralFormField[]
) => ({
  type: COSELL_ACTIONS.SET_REFERRAL_FORM_FIELDS,
  payload: referralFormFields,
})

export const setIsShowRequiredFieldsOnly = (showFields: {
  isShowRequiredFieldsOnly: boolean
}) => ({
  type: COSELL_ACTIONS.SET_SHOW_ALL_FIELDS,
  payload: showFields,
})

export const setCategoriesWithNoFields = (categories: string[]) => ({
  type: COSELL_ACTIONS.SET_CATEGORIES_WITH_NO_FIELDS,
  payload: categories,
})

export interface ReferralsContent {
  referral_metadata: Record<string, unknown>
  referral_object: Record<string, unknown>
  opportunity_recommendation?: Record<string, unknown>
  funding_details?: Record<string, unknown> | null
  funding?: Record<string, unknown> | null
}
export interface ReferralsResponse {
  referrals: ReferralsContent[]
  count: number
  page_number: number
  page_size: number
}

export interface ReferralsData {
  labra_referral: Record<string, unknown>
  opportunity_recommendation?: Record<string, unknown>
  [x: string]: unknown
}

export type CosellReferralType = 'opportunity' | 'lead'
export type CosellReferralBy = 'ace' | 'crm'

export const setFilters = (filters: FiltersState) => ({
  type: COSELL_ACTIONS.SET_FILTERS,
  payload: filters,
})

export const setDashboardMetrics = (dashboardMetrics: DashboardMetrics) => ({
  type: COSELL_ACTIONS.SET_DASHBOARD_METRICS,
  payload: dashboardMetrics,
})

export const setAgentRecommendations = (
  agentRecommendations: AgentRecommendations
) => ({
  type: COSELL_ACTIONS.SET_AGENT_RECOMMENDATIONS,
  payload: agentRecommendations,
})

export const setActiveAgentDrawer = (agentType: AgentDrawerType) => ({
  type: COSELL_ACTIONS.SET_ACTIVE_AGENT_DRAWER,
  payload: agentType,
})

export const clearActiveAgentDrawer = () => ({
  type: COSELL_ACTIONS.CLEAR_ACTIVE_AGENT_DRAWER,
})

const resolveDateFromNow = (
  type: 'targetCloseDate' | 'lastUpdated',
  value: string
): string => {
  const now = new Date()

  const mappings: Record<string, () => Date> = {
    'targetCloseDate-3days': () => addDays(now, 3),
    'targetCloseDate-7days': () => addDays(now, 7),
    'targetCloseDate-30days': () => addDays(now, 30),
    'lastUpdated-24hours': () => subHours(now, 24),
    'lastUpdated-3days': () => subDays(now, 3),
    'lastUpdated-7days': () => subDays(now, 7),
    'lastUpdated-30days': () => subDays(now, 30),
  }

  const key = `${type}-${value}`
  return mappings[key] ? format(mappings[key](), 'yyyy-MM-dd') : ''
}

export const fetchReferralsData =
  ({
    productId,
    pageSize = 10,
    pageNumber,
    referralType = 'opportunity',
    referralBy = undefined,
    sortOrder = 'desc',
    keepPrevious = false,
    fetchMoreData = false,
    isNewCoSellPipeline = false,
    sortBy = isNewCoSellPipeline ? 'updated_at' : 'created_at',
    companyName = '',
    cloudId = '',
    objectId = '',
    crmId = '',
    warning = '',
  }: {
    productId: ProductType
    pageSize?: number
    pageNumber?: number
    referralType?: CosellReferralType
    referralBy?: CosellReferralBy
    sortBy?: string
    sortOrder?: SortDirection
    keepPrevious?: boolean
    fetchMoreData?: boolean
    isNewCoSellPipeline?: boolean
    companyName?: string
    objectId?: string
    crmId?: string
    cloudId?: string
    warning?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    !fetchMoreData &&
      dispatch(startLoading(LoadingTypes.COSELL_FETCH_REFERRALS))

    try {
      const state = getState()
      const currentReferrals =
        state.cosell[productId].referralObjects?.referrals || []
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''
      let response = null
      let referrals = []
      let referralsObject = []
      const { filters } = getState().cosell[productId]

      const formatFilterValue = (value: string) =>
        value === 'All' ? 'all' : value

      // Selections from the "Warning" filter section take precedence over the
      // tab-level default passed in via `warning`. Stored values are display
      // labels, so map them back to the backend codes.
      const warningValue = filters?.warning?.length
        ? filters.warning.map(label => warningMapping[label] ?? label).join(',')
        : warning

      const finalFilters = {
        cloud_acceptance_status:
          filters?.AWSApprovalStatus?.map(formatFilterValue).join(',') || '',
        partner_acceptance_status:
          filters?.acceptanceStatus?.map(formatFilterValue).join(',') || '',
        stage: filters?.stage?.map(formatFilterValue).join(',') || '',
        target_close_date: resolveDateFromNow(
          'targetCloseDate',
          filters?.targetCloseDate || ''
        ),
        updated_at: resolveDateFromNow(
          'lastUpdated',
          filters?.lastUpdated || ''
        ),
        ...(filters?.fundingProgram?.length
          ? { funding_program: filters.fundingProgram.join(',') }
          : {}),
        ...(warningValue ? { warning: warningValue } : {}),
      }
      if (isNewCoSellPipeline) {
        let sortByField = sortBy
        if (sortBy === 'targetCloseDate') {
          sortByField = 'Labra__Target_Close_Date__c'
        } else if (sortBy === 'lastUpdated') {
          sortByField = 'updated_at'
        }

        response = await fetchAWSReferrals(
          partnerId,
          pageSize,
          pageNumber,
          referralType,
          referralBy,
          sortByField,
          companyName,
          objectId,
          crmId,
          cloudId,
          sortOrder,
          finalFilters
        )
        const transformedData: ReferralsContent[] =
          response?.data.referrals.map((item: ReferralsData) => {
            const {
              labra_referral,
              opportunity_recommendation,
              error_message,
              funding_details,
              funding,
              warning,
              ...rest
            } = item
            return {
              referral_metadata: { ...rest },
              referral_object: labra_referral,
              opportunity_recommendation,
              error_message,
              funding_details,
              funding,
              warning,
            }
          })
        referralsObject = getReferralObjectTransformed({
          referrals: transformedData,
        } as ReferralsResponse)
      } else {
        response = await fetchReferrals(
          partnerId,
          pageSize,
          pageNumber,
          referralType,
          referralBy,
          sortBy,
          sortOrder,
          finalFilters
        )
        referralsObject = getReferralObjectTransformed(response?.data)
      }

      referrals = keepPrevious
        ? [...currentReferrals, ...referralsObject]
        : referralsObject
      await dispatch(
        actionTypeWrapper(
          productId,
          setReferrals({
            referral: referrals,
            count: response?.data?.count || 0,
            pageNumber: response?.data?.page_number || 1,
            pageSize: response?.data?.page_size || 10,
            hasMore: isNewCoSellPipeline
              ? referrals.length < response?.data?.count
              : referralsObject.length >= pageSize,
          })
        )
      )
    } catch (e) {
      const errorMessage = getErrorMessages(['Unable to fetch referrals.'])(
        e as AxiosResponse<ErrorResponse>
      )

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_FETCH_REFERRALS))
    }
  }

export const fetchReferralsMetrics =
  (productId: ProductType) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL))

    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''
      const response = await fetchMetrics(partnerId)

      await dispatch(
        actionTypeWrapper(
          productId,
          setReferralsMetrics(camelize(response.data))
        )
      )
    } catch (e) {
      const errorMessage = getErrorMessages(['Unable to fetch referrals.'])(
        e as AxiosResponse<ErrorResponse>
      )

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL))
    }
  }

export const fetchAwsProducts =
  (productId: ProductType, partnerIdOverride?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const reduxState = getState()
    if (reduxState.loading[LoadingTypes.COSELL_FETCH_AWS_PRODUCTS] > 0) return
    if ((reduxState.cosell[productId]?.awsProducts?.length ?? 0) > 0) return

    dispatch(startLoading(LoadingTypes.COSELL_FETCH_AWS_PRODUCTS))

    try {
      const state = getState()
      const partnerId =
        partnerIdOverride ||
        (() => {
          const isAdmin = isAdminRole(state.userProfile.userProfile)
          const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
          return (
            getPartnerIdFromState(
              state,
              window.location.pathname,
              partnerType
            ) || ''
          )
        })()
      const response = await fetchSolutions(partnerId)
      const awsProducts: AwsProducts[] = (
        response.data?.cloud_config?.aws_products ?? []
      ).map((item: ApiResponseProduct) => ({
        ProductCode: item.aws_product_code,
        productName: item.product_name,
        productDescription: item.product_description,
        productFamily: item.product_family,
      }))

      const productOptionsValues = awsProducts.map((item: AwsProducts) => {
        return { label: item.productName, value: item.ProductCode }
      }) as SelectorOptions[]
      await dispatch(
        actionTypeWrapper(productId, setAwsProducts(productOptionsValues))
      )
    } catch (e) {
      const errorMessage = getErrorMessages(['Unable to fetch aws products.'])(
        e as AxiosResponse<ErrorResponse>
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_FETCH_AWS_PRODUCTS))
    }
  }

export const fetchPartnerCentralUsersData =
  (productId: ProductType) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_PARTNER_CENTRAL_USERS_DATA))

    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''
      const response = await fetchPartnerCentralUsers(partnerId)

      await dispatch(
        actionTypeWrapper(
          productId,
          setPartnerCentralUsersData(camelize(response.data))
        )
      )
    } catch (e) {
      const errorMessage = getErrorMessages(['Unable to fetch aws products.'])(
        e as AxiosResponse<ErrorResponse>
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_PARTNER_CENTRAL_USERS_DATA))
    }
  }

export const fetchTestOpportunityData =
  (productId: ProductType, referralId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))

    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''
      const response = await fetchTestOpportunity(partnerId, referralId)

      await dispatch(
        actionTypeWrapper(productId, setTestOpportunityData(response.data))
      )
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch test opportunity.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))
    }
  }

export const saveTestOpportunityData =
  (productId: ProductType, referralId: string, testOpportunityData: object) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))

    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''

      const response = await putTestOpportunity(
        partnerId,
        referralId,
        testOpportunityData
      )

      if (!isEmpty(response.data)) {
        await dispatch(
          actionTypeWrapper(
            productId,
            setTestOpportunityData(testOpportunityData)
          )
        )
        dispatch(
          updateAppAlert({
            message: TestOpportunitySaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      }
    } catch (e) {
      const errorMessage = getErrorMessages(['Unable to sent updates.'])(
        e as AxiosResponse<ErrorResponse>
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))
    }
  }

/**
 * Helper function to check if we're running in Salesforce context
 * Uses canvasAuthService
 */
const isInSalesforceContext = (): boolean => {
  try {
    const partnerId = canvasAuthService.getPartnerId()
    const isSalesforce = !!(partnerId && !isEmpty(partnerId))
    return isSalesforce
  } catch (error) {
    console.error('[isInSalesforceContext] Error:', error)
    return false
  }
}

export const fetchSolutionOffered =
  (productId: ProductType, crmId: string, templateId?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_FETCH_SOLUTIONS_OFFERED))

    try {
      // Check if we're in Salesforce context
      const inSalesforceContext = isInSalesforceContext()

      if (inSalesforceContext) {
        // In Salesforce context, crmId parameter is ignored - we use partnerId instead
        // In Salesforce, use fetchSolutions API with partnerId instead of fetchCRMfiels
        /*const state = getState()*/
        const partnerId = canvasAuthService.getPartnerId()
        /*const statePartnerId = state.AccountData.user.partnerData?.partnerId*/
        /*const partnerId = canvasPartnerId*/

        if (!partnerId) {
          console.error('Partner ID is missing in Salesforce context')
          throw new Error('Partner ID is missing in Salesforce context')
        }

        const response = await fetchSolutions(partnerId)

        const awsSolutions = response.data.cloud_config?.aws_solutions || []
        const solutionOptions = mapAwsSolutionsToSelectorOptions(awsSolutions)
        await dispatch(
          actionTypeWrapper(
            productId,
            setSolutionOfferedOptions(solutionOptions)
          )
        )
      } else {
        // For non-Salesforce contexts (e.g., HubSpot), use fetchCRMfiels
        if (!crmId || isEmpty(crmId)) {
          console.error('crmId is missing or empty for non-Salesforce context')
          throw new Error('CRM ID is missing or empty')
        }

        const response = await fetchCRMfiels(crmId, templateId)

        const solutions = response.data.find(
          (r: FormField) => r.id === 'Labra__Solution_offered__c'
        )?.options as SelectorOptions[]

        if (solutions) {
          await dispatch(
            actionTypeWrapper(productId, setSolutionOfferedOptions(solutions))
          )
        }

        await dispatch(
          actionTypeWrapper(
            productId,
            setReferralFormFields(camelize(response.data))
          )
        )

        const partnerId = getPartnerIdForCosellActions(getState)
        if (partnerId) {
          try {
            await loadSolutionOfferedFromAwsEngine(
              dispatch,
              productId,
              partnerId,
              getState
            )
          } catch (e) {
            errorLogger({ globalState: getState() })(e as Error)
          }
        }
      }
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch solution offered.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_FETCH_SOLUTIONS_OFFERED))
    }
  }
export const createIncomingTestOpportunities =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))
    try {
      if (partnerId) {
        await createTestOpportunity(partnerId)
      }
      dispatch(
        updateAppAlert({
          message: 'Test opportunities created successfully.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to create test opportunities.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))
    }
  }
export const acceptAllOutgoingTestOpportunities =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))
    try {
      if (partnerId) {
        await approveTestOpportunity(partnerId)
      }
      dispatch(
        updateAppAlert({
          message: 'Test opportunities approved successfully.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to accept test opportunities.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_TEST_OPPORTUNITY))
    }
  }

export const reSyncReferral =
  (partnerId: string, referralId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_RESYNC_REFERRAL))
    try {
      if (partnerId && referralId) {
        await reSyncReferralAPI(partnerId, referralId)
        dispatch(
          updateAppAlert({
            message: 'The re-sync referral was made successfully.',
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      }
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to re-sync the referral.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_RESYNC_REFERRAL))
    }
  }
export const fetchDashboardMetrics =
  (productId: ProductType) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_FETCH_DASHBOARD_METRICS))

    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''
      const response = await fetchDashboardMetricsAPI(partnerId)

      await dispatch(
        actionTypeWrapper(
          productId,
          setDashboardMetrics(camelize(response.data))
        )
      )
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch dashboard metrics.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_FETCH_DASHBOARD_METRICS))
    }
  }

export const fetchSolutionsOffered =
  (
    productId: ProductType,
    partnerId: string,
    fetchOptions?: FetchSolutionsOfferedOptions
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const st = getState()
    if (st.loading[LoadingTypes.COSELL_FETCH_SOLUTIONS_OFFERED] > 0) return
    if (
      solutionOfferedOptionsHaveAwsStatus(
        st.cosell[productId]?.solutionOfferedOptions
      )
    ) {
      return
    }

    dispatch(startLoading(LoadingTypes.COSELL_FETCH_SOLUTIONS_OFFERED))

    try {
      await loadSolutionOfferedFromAwsEngine(
        dispatch,
        productId,
        partnerId,
        getState
      )
    } catch (e) {
      if (!fetchOptions?.silent) {
        const errorMessage = getErrorMessages([
          'Unable to fetch solutions offered.',
        ])(e as AxiosResponse<ErrorResponse>)
        dispatch(
          updateAppAlert({
            message: errorMessage,
            messageType: 'ERROR',
          })
        )
      }
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_FETCH_SOLUTIONS_OFFERED))
    }
  }

export const refreshSolutionOfferedByPartnerId =
  (partnerId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<{ success: boolean }> => {
    const productId: ProductType = 'oppsync'

    try {
      if (!partnerId) {
        throw new Error('Partner ID is missing.')
      }

      await getPartnerSolutions(partnerId, { updateCache: true })

      // Reload dropdown from engine service
      const response = await fetchSolutions(partnerId)
      const awsSolutions = response.data.cloud_config?.aws_solutions || []
      const existingOptions =
        getState().cosell[productId]?.solutionOfferedOptions ?? []
      const solutionOptions =
        existingOptions.length > 0
          ? enrichSolutionOfferedOptionsWithAwsStatus(
              existingOptions,
              awsSolutions
            )
          : mapAwsSolutionsToSelectorOptions(awsSolutions)

      await dispatch(
        actionTypeWrapper(productId, setSolutionOfferedOptions(solutionOptions))
      )

      dispatch(
        updateAppAlert({
          message: 'Solutions refreshed successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return { success: true }
    } catch (e) {
      const errorMessage =
        e instanceof Error
          ? e.message
          : getErrorMessages([
              'Failed to refresh solutions. Please try again.',
            ])(e as AxiosResponse<ErrorResponse>)

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      errorLogger({ globalState: getState() })(e as Error)
      return { success: false }
    }
  }

export const fetchAgentRecommendations =
  (productId: ProductType) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COSELL_FETCH_AGENT_RECOMMENDATIONS))

    try {
      const state = getState()
      const isAdmin = isAdminRole(state.userProfile.userProfile)
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      const partnerId =
        getPartnerIdFromState(state, window.location.pathname, partnerType) ||
        ''
      const response = await fetchAgentRecommendationsAPI(partnerId)

      await dispatch(
        actionTypeWrapper(
          productId,
          setAgentRecommendations(camelize(response.data))
        )
      )
    } catch (e) {
      const errorMessage = getErrorMessages([
        'Unable to fetch agent recommendations.',
      ])(e as AxiosResponse<ErrorResponse>)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COSELL_FETCH_AGENT_RECOMMENDATIONS))
    }
  }
